import { useState, useEffect } from "react";
import { fetchModuleConfig, saveModuleConfig } from "../api/index";

export const MODULE_DEFINITIONS = [
  {
    group: "General",
    items: [
      { key: "dashboard",    label: "Dashboard",       description: "Analytics and KPI overview dashboard." },
      { key: "todos",        label: "To-Do Lists",      description: "Task management and personal to-do lists." },
    ],
  },
  {
    group: "Core Operations",
    items: [
      { key: "accounting",   label: "Accounting",       description: "Invoices, payments, journals, chart of accounts, reports, vouchers, and credit/debit notes." },
      { key: "pos",          label: "Point of Sale",    description: "POS terminal for in-person sales." },
      { key: "purchases",    label: "Purchases",        description: "Purchase invoices and supplier management." },
      { key: "customers",    label: "Customers",        description: "Customer records and relationship management." },
      { key: "inventory",    label: "Inventory",        description: "Stock items, warehouses, transfers, adjustments, and inventory reports." },
      { key: "projects",     label: "Projects",         description: "Project tracking and management." },
      { key: "assets",       label: "Assets",           description: "Fixed asset register and depreciation." },
      { key: "costCenters",  label: "Cost Centers",     description: "Cost center and profit center tracking." },
    ],
  },
  {
    group: "Business Modules",
    items: [
      { key: "construction", label: "Construction",     description: "Construction project management and tracking." },
      { key: "manufacturing", label: "Manufacturing",   description: "Plan, build, inspect, cost, and report production work." },
      { key: "lawFirm",      label: "Law Firm",         description: "Legal case and matter management." },
      { key: "clinic",       label: "Clinic",           description: "Medical services, patients, and appointments." },
      { key: "rentals",      label: "Rentals",          description: "Rental property and contract management." },
      { key: "properties",   label: "Properties",       description: "Real estate property listings and management." },
    ],
  },
  {
    group: "Management",
    items: [
      { key: "attendance",   label: "Attendance",       description: "Employee attendance tracking and timesheets." },
      { key: "payrolls",     label: "Payrolls",         description: "Payroll processing and salary management." },
      { key: "letters",      label: "Letters",          description: "Official letter generation and templates." },
      { key: "importExport", label: "Import / Export",  description: "Bulk data import and export via Excel." },
      { key: "excel",        label: "Excel Workbooks",  description: "Personal spreadsheet workbooks with saved files for each user." },
      { key: "website",      label: "My Website",       description: "Public e-commerce storefront builder." },
    ],
  },
  {
    group: "Utilities",
    items: [
      { key: "chat",           label: "Chat",                  description: "Internal messaging and team chat." },
      { key: "workflow",       label: "Workflow",               description: "Business process workflows and approval inbox." },
      { key: "qrCode",         label: "QR Code Generator",     description: "Generate QR codes for products and links." },
      { key: "communications", label: "Communications",         description: "SMS and communication center." },
    ],
  },
];

export const DEFAULTS = MODULE_DEFINITIONS
  .flatMap((g) => g.items)
  .reduce((acc, { key }) => ({ ...acc, [key]: true }), {});

const STORAGE_KEY  = "moduleConfig";
const TS_KEY       = "moduleConfig_ts";
const EVENT_NAME   = "modulesChanged";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Read merged config from localStorage (instant, no API)
export const getModules = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { ...DEFAULTS, ...JSON.parse(saved) };
  } catch {}
  return { ...DEFAULTS };
};

// Write to localStorage and broadcast to all useModules() instances
const applyLocal = (config) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  localStorage.setItem(TS_KEY, Date.now().toString());
  window.dispatchEvent(new Event(EVENT_NAME));
};

// Admin: apply locally first (NavBar updates immediately), then sync to backend
export const saveModules = async (config) => {
  applyLocal(config);
  await saveModuleConfig(config);
};

// Admin: reset to all-enabled
export const resetModules = async () => {
  applyLocal(DEFAULTS);
  await saveModuleConfig(DEFAULTS);
};

// Hook — used by NavBar and any component that needs module visibility
export const useModules = () => {
  const [modules, setModules] = useState(getModules);

  useEffect(() => {
    // Fetch from backend if cache is stale or empty
    const lastFetch = parseInt(localStorage.getItem(TS_KEY) || "0", 10);
    if (Date.now() - lastFetch > CACHE_TTL_MS) {
      fetchModuleConfig()
        .then((r) => {
          const remote = r.data;
          // Always stamp TTL so we don't re-fetch on every mount
          localStorage.setItem(TS_KEY, Date.now().toString());
          if (remote && typeof remote === "object" && Object.keys(remote).length > 0) {
            const merged = { ...DEFAULTS, ...remote };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            setModules(merged);
          }
        })
        .catch(() => {
          // Backend unreachable — keep using cached localStorage value
        });
    }

    const handler = () => setModules(getModules());
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []); // runs once per component mount (NavBar stays mounted for the session)

  return modules;
};
